import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { lmsAdmin } from '../api'
import { usePresetAccess } from './access'
import '../lms-admin.scss'
import { LmsIcon } from '../lms-icon'
import { ScoreReportingFields, draftError, draftOf, scoringOf } from '../score-reporting'
import { PageTrail } from '../../../shared/sso-workspace/page-trail'
import { SECTION } from '../../../shared/navigation/sections'
import { IconAdd, IconEdit } from '../../../shared/icon/icon'
import { DockedSaveBar } from '../../../shared/sso-workspace/save-bar'
import { LeaveGuard } from '../../../shared/sso-workspace/leave-guard'

// Preset Editor (docs/08 §7, design edxr-preset-editor-v1.0.html) —
// create/edit one platform preset. The backend exposes list/create/patch
// only, so edit mode loads via the list. Config is the raw preset JSON
// (deep-link schemes, writeback quirks) edited as validated JSON text.
// Save and Discard are the admin's docked save bar (save-bar.js; owner
// 2026-10-23), up while the form differs from the preset as stored (a new
// one's, from the empty form); the back link leaves, asking first while
// something is unsaved.

const PRESETS_PATH = '/org/lms/presets'

// The form as a new preset starts.
const EMPTY_FORM = {
  slug: '',
  name: '',
  description: '',
  family: '',
  scormVersion: 'scorm-1.2',
  status: 'draft',
  configText: '{}',
  scoring: draftOf(null)
}

// The form of a stored preset.
const formOf = preset => ({
  slug: preset.slug,
  name: preset.name,
  description: preset.description || '',
  family: preset.lms_family || '',
  scormVersion: preset.scorm_version,
  status: preset.status,
  configText: JSON.stringify(preset.config || {}, null, 2),
  scoring: draftOf(preset.scoring)
})

// The fields that differ between two forms, one entry each (the save bar's
// count). Text is compared as it will be sent (trimmed); the config as typed.
export const presetChanges = (form, saved) =>
  ['slug', 'name', 'description', 'family'].filter(field => String(form[field]).trim() !== String(saved[field]).trim())
    .concat(['scormVersion', 'status', 'configText'].filter(field => form[field] !== saved[field]))
    .concat(JSON.stringify(form.scoring) !== JSON.stringify(saved.scoring) ? ['scoring'] : [])

// The version a connection built from this preset delivers as SCORM. A
// connection that delivers cmi5 builds cmi5 whatever its preset says.
const SCORM_VERSIONS = [
  ['scorm-1.2', 'SCORM 1.2 (default)'],
  ['scorm-2004-4ed', 'SCORM 2004 4th Edition']
]

// Exported so the list's pill map can be checked against it.
export const STATUSES = [
  ['draft', 'Draft', 'Not offered to organizations yet.'],
  ['published', 'Published', 'Organizations can create connections from it.'],
  ['archived', 'Archived', 'Hidden from new connections; existing ones keep working.']
]

// The fields the editor shows an inline error under; a refused field not in
// this list still shows the banner, so no server message goes unseen.
const INLINE_FIELDS = [
  'name',
  'slug',
  'description',
  'lms_family',
  'scorm_version',
  'config',
  'status',
  'scoring.pass_rule',
  'scoring.score_rule',
  'scoring.pass_mark'
]

export const PresetEditor = ({ mode }) => {
  const history = useHistory()
  const { presetId } = useParams()
  const { canEdit, reason } = usePresetAccess()
  const isEdit = mode === 'edit'

  const [slug, updateSlug] = useState('')
  const [name, updateName] = useState('')
  // the name as stored, for the trail (the input's live text is not a crumb)
  const [savedName, updateSavedName] = useState('')
  const [description, updateDescription] = useState('')
  const [family, updateFamily] = useState('')
  const [scormVersion, updateScormVersion] = useState('scorm-1.2')
  const [status, updateStatus] = useState('draft')
  const [configText, updateConfigText] = useState('{}')
  const [configError, updateConfigError] = useState(null)
  // How the courses of this preset's connections report their score and
  // pass (LMS Phase F scoring): the defaults for a new preset.
  const [scoring, updateScoring] = useState(draftOf(null))
  const [scoringError, updateScoringError] = useState(null)
  const [error, updateError] = useState(null)
  const [busy, updateBusy] = useState(false)
  // The form as stored (a new preset's, empty): what Discard puts back.
  const [savedForm, updateSavedForm] = useState(EMPTY_FORM)

  // The server names each refused field (400 VALIDATION_ERROR, or the 409 of
  // a taken slug: error.details.fields): shown under the field. Any other
  // error is the banner's.
  const fieldErrors = (error && error.details && error.details.fields) || {}
  const unshownFields = Object.keys(fieldErrors).filter(name => !INLINE_FIELDS.includes(name))
  const fieldError = name => fieldErrors[name] && <div className="field-error" data-testid={`preset-error-${name}`}>{fieldErrors[name]}</div>

  useEffect(() => {
    if (!isEdit) return
    lmsAdmin
      .listPresets()
      .then(result => {
        const preset = (result.presets || []).find(p => String(p.id) === String(presetId))
        if (!preset) {
          updateError(new Error('Preset not found'))
          return
        }
        const form = formOf(preset)
        applyForm(form)
        updateSavedForm(form)
        updateSavedName(preset.name)
      })
      .catch(updateError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, presetId])

  const applyForm = form => {
    updateSlug(form.slug)
    updateName(form.name)
    updateDescription(form.description)
    updateFamily(form.family)
    updateScormVersion(form.scormVersion)
    updateStatus(form.status)
    updateConfigText(form.configText)
    updateScoring(form.scoring)
  }

  const form = { slug, name, description, family, scormVersion, status, configText, scoring }
  const unsavedCount = canEdit ? presetChanges(form, savedForm).length : 0
  const dirty = unsavedCount > 0

  // The save bar's Discard: the form as stored, and no errors.
  const discard = () => {
    if (busy) return
    applyForm(savedForm)
    updateError(null)
    updateConfigError(null)
    updateScoringError(null)
  }

  const save = async () => {
    let config
    try {
      config = JSON.parse(configText || '{}')
      updateConfigError(null)
    } catch (e) {
      updateConfigError('Config must be valid JSON.')
      return
    }
    const markError = draftError(scoring)
    updateScoringError(markError)
    if (markError) return
    updateBusy(true)
    try {
      const payload = {
        slug: slug.trim(),
        name: name.trim(),
        description: description.trim(),
        lms_family: family.trim(),
        scorm_version: scormVersion,
        config,
        status,
        scoring: scoringOf(scoring)
      }
      if (isEdit) await lmsAdmin.patchPreset(presetId, payload)
      else await lmsAdmin.createPreset(payload)
      history.push(PRESETS_PATH)
    } catch (e) {
      updateError(e)
      updateBusy(false)
    }
  }

  return (
    <div className="lms-admin" data-testid="preset-editor">
      {/* Platform-wide presets, an Internal tools entry: the trail starts at
          LMS presets, never at the current organization's LMS (audit F11). */}
      <PageTrail
        trail={[
          SECTION.lmsPresets,
          isEdit ? { url: `/org/lms/presets/${presetId}`, text: savedName, Icon: IconEdit } : { url: '/org/lms/presets/new', text: 'New preset', Icon: IconAdd }
        ]}
      />
      <p className="mb-2">
        <Link to={PRESETS_PATH} data-testid="preset-back">
          ← All presets
        </Link>
      </p>
      {/* busy: a save in flight is on its way to the list, so the guard is off for
          it and back on if the service refuses it. */}
      <LeaveGuard when={dirty && !busy} message="Leave this preset without saving your changes?" />
      <div className="drawer-pane lms-page-card">
        <div className="drawer-head">
          <h2 className="drawer-title">{isEdit ? `Edit preset${name ? ` — ${name}` : ''}` : 'New preset'}</h2>
          <p className="drawer-sub">Changes apply to every organization connection built from this preset. System scope — audited.</p>
        </div>

        <div className="drawer-body">
          {!canEdit && reason && (
            <div className="alert alert-warning" role="note" data-testid="preset-read-only">
              {reason}
            </div>
          )}

          {error && (Object.keys(fieldErrors).length === 0 || unshownFields.length > 0) && (
            // the save bar's words are the one alert while it is up
            <div className="error-banner" data-testid="preset-error" role={dirty ? undefined : 'alert'}>
              <LmsIcon name="circle-exclamation" />
              <span>
                {error.message}
                {unshownFields.map(name => ` · ${name}: ${fieldErrors[name]}`).join('')}
              </span>
            </div>
          )}

          {(isEdit || canEdit) && (
            <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
              <div className="form-section">
                <div className="form-section-title">Identity</div>
                <div className="field">
                  <label className="field-label">Name</label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    placeholder="Moodle 4.x"
                    value={name}
                    data-testid="preset-name"
                    onChange={e => updateName(e.target.value)}
                  />
                  {fieldError('name')}
                </div>
                <div className="field">
                  <label className="field-label">Slug</label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    placeholder="moodle-4x"
                    value={slug}
                    data-testid="preset-slug"
                    onChange={e => updateSlug(e.target.value)}
                  />
                  {fieldError('slug')}
                  <div className="field-help">Unique, stable identifier — referenced by connections and audit entries.</div>
                </div>
                <div className="field">
                  <label className="field-label">Description</label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    placeholder="Open-source LMS · SCORM activity upload"
                    value={description}
                    onChange={e => updateDescription(e.target.value)}
                  />
                  {fieldError('description')}
                </div>
                <div className="field">
                  <label className="field-label">LMS family</label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    placeholder="moodle"
                    value={family}
                    data-testid="preset-family"
                    onChange={e => updateFamily(e.target.value)}
                  />
                  {fieldError('lms_family')}
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">SCORM</div>
                <div className="field">
                  <label className="field-label">SCORM version</label>
                  <select
                    className="form-select form-select-sm"
                    value={scormVersion}
                    data-testid="preset-scorm"
                    onChange={e => updateScormVersion(e.target.value)}
                  >
                    {SCORM_VERSIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <div className="field-help" data-testid="preset-scorm-hint">
                    The package a connection builds from this preset. The admin does not choose it again when building or
                    downloading. A connection that delivers cmi5 builds cmi5 whatever is chosen here.
                  </div>
                  {fieldError('scorm_version')}
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-title">Config</div>
                <div className="field">
                  <label className="field-label">Preset config (JSON)</label>
                  <textarea
                    className="form-control form-control-sm text-mono"
                    rows={10}
                    style={{ fontSize: 12 }}
                    value={configText}
                    data-testid="preset-config"
                    onChange={e => updateConfigText(e.target.value)}
                  />
                  {configError && <div className="field-error">{configError}</div>}
                  {fieldError('config')}
                  <div className="field-help">
                    Deep-link schemes, writeback quirks and token lifetimes, as a JSON object.
                  </div>
                </div>
              </div>

              <div className="form-section" data-testid="preset-scoring">
                <div className="form-section-title">How the course score and pass are reported</div>
                <ScoreReportingFields
                  name="preset-scoring"
                  draft={scoring}
                  disabled={!canEdit}
                  onChange={next => {
                    updateScoring(next)
                    updateScoringError(null)
                  }}
                  errors={{
                    pass_rule: fieldErrors['scoring.pass_rule'],
                    score_rule: fieldErrors['scoring.score_rule'],
                    pass_mark: scoringError || fieldErrors['scoring.pass_mark']
                  }}
                />
              </div>

              <div className="form-section">
                <div className="form-section-title">Status</div>
                <div className="radio-group">
                  {STATUSES.map(([value, label, desc]) => (
                    <label key={value} className={`radio-option ${status === value ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="preset-status"
                        value={value}
                        checked={status === value}
                        data-testid={`status-${value}`}
                        onChange={() => updateStatus(value)}
                      />
                      <div className="body">
                        <div className="name">{label}</div>
                        <div className="desc">{desc}</div>
                      </div>
                    </label>
                  ))}
                  {fieldError('status')}
                </div>
              </div>
            </fieldset>
          )}
        </div>

      </div>
      {canEdit && (
        <DockedSaveBar
          testId="preset-savebar"
          saveTestId="preset-save"
          discardTestId="preset-discard"
          on={dirty}
          count={unsavedCount}
          error={dirty ? (error ? error.message : configError || scoringError ? "Can't save yet: fix the highlighted fields." : null) : null}
          failure={error}
          busy={busy}
          canSave={Boolean(name.trim() && slug.trim())}
          onSave={save}
          onDiscard={discard}
          saveLabel={isEdit ? 'Save changes' : 'Create preset'}
          busyLabel={isEdit ? 'Saving…' : 'Creating…'}
        />
      )}
    </div>
  )
}

export const PresetEditorNew = () => <PresetEditor mode="new" />
export const PresetEditorEdit = () => <PresetEditor mode="edit" />
